import { type ImgProxyConstraintOptions } from '@/types/Document';
import { buildImgProxyQueryString } from '@/utils/helpers';
import axios from 'axios';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import log from 'loglevel';

class DocumentService {
    private readonly apiBaseUrl;

    constructor() {
        this.apiBaseUrl = import.meta.env.VITE_API_STORAGE_URL || '';
    }

    async getAllFiles(page = 1, pageSize = 10) {
        try {
            const response = await axios.get(`${this.apiBaseUrl}`, {
                params: {
                    page,
                    pageSize,
                },
            });
            return response.data;
        } catch (error) {
            log.error('Error fetching all files:', error);
            throw error;
        }
    }

    storeFile = async (
        file: File,
        imgConstraints: ImgProxyConstraintOptions = { watermark: true, quality75: true },
    ) => {
        try {
            const { watermark = true, quality75 = true, ...rest } = imgConstraints;
            const constraints = { watermark, quality75, ...rest };

            const queryParams = new URLSearchParams();
            queryParams.append('watermark', String(constraints.watermark));
            queryParams.append('quality75', String(constraints.quality75));

            Object.keys(rest).forEach((key) => {
                queryParams.append(key, String((rest as any)[key]));
            });

            const url = `${this.apiBaseUrl}?${queryParams.toString()}`;

            const formData = new FormData();
            formData.append('data', file);

            const response = await axios.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    accept: 'application/json',
                },
            });

            return response.data;
        } catch (e) {
            log.error(e);
            if (axios.isAxiosError(e)) {
                const error = { axiosError: true };
                throw error;
            }
            log.error('Error storing file:', e);
            throw e;
        }
    };

    getFileByIdToBlob = async (
        id: string,
        imgConstraints: ImgProxyConstraintOptions = {},
        format: string | null = null,
    ) => {
        try {
            let queryString = '';
            if (!format) {
                queryString = buildImgProxyQueryString(imgConstraints);
            }
            const url = `${this.apiBaseUrl}/${id}${queryString ? `?${queryString}` : ''}`;

            const response = await axios.get(url, {
                responseType: format === 'json' ? 'json' : 'blob',
            });

            if (format === 'zip') {
                const zipInstance = new JSZip();
                const fileName = `${id}.zip`;

                zipInstance.file(fileName, response.data);

                const zipBlob = await zipInstance.generateAsync({ type: 'blob' });
                const zipUrl = URL.createObjectURL(zipBlob);
                if (process.client) {
                    saveAs(zipBlob, `${id}.zip`);
                }

                return zipUrl;
            } else if (format === 'json') {
                const jsonString = JSON.stringify(response.data, null, 2);
                const jsonBlob = new Blob([jsonString], { type: 'application/json' });
                const fileName = `${id}.json`;
                if (process.client) {
                    saveAs(jsonBlob, fileName);
                }

                return fileName;
            } else {
                return URL.createObjectURL(response.data);
            }
        } catch (error) {
            log.error(`Error fetching file by ID (${id}):`, error);
            throw error;
        }
    };

    getFileById = (id: string, imgConstraints: ImgProxyConstraintOptions = {}) => {
        try {
            const queryString = buildImgProxyQueryString(imgConstraints);

            return `${this.apiBaseUrl}/${id}${queryString ? `?${queryString}` : ''}`;
        } catch (error) {
            log.error(`Error fetching file by ID (${id}):`, error);
            throw error;
        }
    };

    async deleteFileById(id: string) {
        try {
            const response = await axios.delete(`${this.apiBaseUrl}/${id}`);
            return response.data;
        } catch (error) {
            log.error(`Error deleting file by ID (${id}):`, error);
            throw error;
        }
    }

    async patchFile(id: string, fileData: File) {
        try {
            const response = await axios.patch(`${this.apiBaseUrl}/${id}`, fileData);
            return response.data;
        } catch (error) {
            log.error(`Error patching file by ID (${id}):`, error);
            throw error;
        }
    }

    downloadFile = async (path: string, filename = 'preview') => {
        try {
            const response = await axios.get(path, {
                responseType: 'blob',
            });
            if (process.client) {
                saveAs(response.data, filename);
            }
        } catch (error) {
            log.error(`Error downloading file from ${path}:`, error);
            throw error;
        }
    };

    async cleanOldFiles() {
        try {
            const response = await axios.get(`${this.apiBaseUrl}/clean`);
            return response.data;
        } catch (error) {
            log.error('Error cleaning old temporary files:', error);
            throw error;
        }
    }
}

export default new DocumentService();
